"use client";

import { Suspense, useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";

import { Button } from "@workspace/sc-ui/components/button";
import { cn } from "@workspace/sc-ui/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@workspace/sc-ui/components/tooltip";

import { Locale } from "@/app/[locale]/layout";
import { Link, usePathname } from "@/i18n/navigation";

interface LanguageSwitcherProps {
  className?: string;
}

function SwitcherButton({
  className,
  targetLocale,
  label,
  href,
  locale,
}: {
  className?: string;
  targetLocale: string;
  label: string;
  href?: string;
  locale?: Locale;
}) {
  const labelClasses =
    "rounded-md border border-border px-3 py-1 text-xs font-medium uppercase tracking-wide transition hover:bg-muted";

  if (href && locale) {
    return (
      <Button className={cn(className)} variant="outline" asChild>
        <Link href={href} locale={locale} className={labelClasses} aria-label={label}>
          {targetLocale}
        </Link>
      </Button>
    );
  }

  // Placeholder: same Button + label shape as the interactive Link (SSR/CSR hydration-safe)
  return (
    <Button className={cn(className)} variant="outline" disabled aria-label={label}>
      <span className={labelClasses}>{targetLocale}</span>
    </Button>
  );
}

const LanguageSwitcherContent = ({ className }: LanguageSwitcherProps) => {
  const locale = useLocale() as Locale;
  const targetLocale = locale === "pl" ? "en" : "pl";
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const t = useTranslations("app.tooltip");

  const search = searchParams.toString();
  const fullPath = search ? `${pathname}?${search}` : pathname;
  const label = t("languageTarget", { locale: targetLocale.toUpperCase() });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <SwitcherButton
          className={className}
          targetLocale={targetLocale}
          label={label}
          href={fullPath}
          locale={targetLocale}
        />
      </TooltipTrigger>
      <TooltipContent>
        <p>{label}</p>
      </TooltipContent>
    </Tooltip>
  );
};

function LanguageSwitcherPlaceholder({ className }: LanguageSwitcherProps) {
  const locale = useLocale() as Locale;
  const targetLocale = locale === "pl" ? "en" : "pl";
  const t = useTranslations("app.tooltip");
  const label = t("languageTarget", { locale: targetLocale.toUpperCase() });

  return <SwitcherButton className={className} targetLocale={targetLocale} label={label} />;
}

export const LanguageSwitcher = (props: LanguageSwitcherProps) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // SSR + first client paint: identical placeholder → no hydration mismatch.
  // After mount: Suspense-safe interactive switcher with useSearchParams.
  if (!mounted) {
    return <LanguageSwitcherPlaceholder {...props} />;
  }

  return (
    <Suspense fallback={<LanguageSwitcherPlaceholder {...props} />}>
      <LanguageSwitcherContent {...props} />
    </Suspense>
  );
};
